import './styles.css';
import './mapStudio.css';
import './rangeNumberControls.js';
import { VehicleCalibrator } from '../../src/tools/vehicleCalibrator.js';
import { WHEELS } from '../../src/game/data.js';
import { CatalogRepository } from './catalogRepository.js';
import { createCatalogProceduralWheel, WheelEditor } from './wheelEditor.js';
import { CannonEditor } from './cannonEditor.js';
import { EngineSoundEditor } from './engineSoundEditor.js';
import { ProjectileEditor } from './projectileEditor.js';
import { TombstoneEditor } from './tombstoneEditor.js';
import { ExplosionEditor, NitroEditor } from './effectEditors.js';
import {
  createEditorThumbnailFile,
  createEngineSoundThumbnailFile,
} from './thumbnailGenerator.js';

const $=(selector,root=document)=>root.querySelector(selector);
const repository=new CatalogRepository();
let mapStudio=null;
let openingMapStudio=false;
document.querySelector('#studio-open-maps').addEventListener('click',async()=>{
  if(openingMapStudio||!repository.canRead())return;
  openingMapStudio=true;
  try{
    if(!mapStudio){
      const {MapStudio}=await import('./mapStudio.js');
      mapStudio=new MapStudio({auth:repository,onClose:()=>elements.catalog.classList.remove('hidden')});
    }
    await mapStudio.open();
    if(repository.canRead())elements.catalog.classList.add('hidden');
  }catch(error){setCloudMessage(error.message,'error');}
  finally{openingMapStudio=false;}
});
const publishesToR2=Boolean(String(import.meta.env.VITE_CATALOG_PUBLISH_API_URL||'').trim());
const allowsFirstAdminSetup=new URLSearchParams(location.search).get('setup')==='first-admin';
const state={items:[],type:'vehicle',editingId:null,authReady:false};
const THUMBNAIL_TYPES=new Set([
  'vehicle',
  'wheel',
  'engine_sound',
  'cannon',
  'projectile',
  'nitro',
  'explosion',
  'tombstone',
]);
const CREATE_LABELS={
  vehicle:'+ NUEVO AUTO',
  wheel:'+ NUEVA RUEDA',
  engine_sound:'+ NUEVO SONIDO',
  cannon:'+ NUEVO CAÑÓN',
  projectile:'+ NUEVO PROYECTIL',
  nitro:'+ NUEVO NITRO',
  explosion:'+ NUEVA EXPLOSIÓN',
  tombstone:'+ NUEVA LÁPIDA',
};
const EMPTY_COPY={
  vehicle:[
    'Todavía no hay autos en este catálogo.',
    'Creá uno, cargá su carrocería, calibralo con Cromo Clásico o una rueda GLB opcional y guardalo.',
  ],
  wheel:[
    'Todavía no hay ruedas en este catálogo.',
    'Partí de una plantilla procedural, ajustá su forma y materiales o importá una rueda GLB.',
  ],
  engine_sound:[
    'Todavía no hay sonidos de motor en este catálogo.',
    'Partí de un preset y ajustá tono, subida de revoluciones, filtro, pulsación y mezcla.',
  ],
  cannon:[
    'Todavía no hay cañones en este catálogo.',
    'Partí de uno de los cuatro estilos del juego y ajustá su estructura, colores y materiales.',
  ],
  projectile:[
    'Todavía no hay proyectiles en este catálogo.',
    'Creá una forma procedural o importá un GLB y diseñá por separado sus sonidos de viaje e impacto.',
  ],
  nitro:[
    'Todavía no hay efectos de nitro en este catálogo.',
    'Diseñá una estela animada, elegí sus boquillas y ajustá partículas, dispersión, velocidad y color.',
  ],
  explosion:[
    'Todavía no hay explosiones en este catálogo.',
    'Combiná partículas, potencia, gravedad, destello y onda expansiva en una vista animada.',
  ],
  tombstone:[
    'Todavía no hay lápidas en este catálogo.',
    'Partí de una placa, cono, holograma o construcción de bloques y personalizá sus piezas.',
  ],
};

const elements={
  catalog:$('#catalog-screen'),
  calibrator:$('#calibrator-screen'),
  wheelEditor:$('#wheel-editor-screen'),
  engineSoundEditor:$('#engine-sound-editor-screen'),
  cannonEditor:$('#cannon-editor-screen'),
  projectileEditor:$('#projectile-editor-screen'),
  nitroEditor:$('#nitro-editor-screen'),
  explosionEditor:$('#explosion-editor-screen'),
  tombstoneEditor:$('#tombstone-editor-screen'),
  list:$('#catalog-list'),
  count:$('#catalog-count'),
  title:$('#catalog-title'),
  empty:$('#catalog-empty'),
  newItem:$('#catalog-new'),
  generateThumbnails:$('#catalog-generate-thumbnails'),
  configureEconomy:$('#catalog-configure-economy'),
  search:$('#catalog-search'),
  name:$('#catalog-display-name'),
  description:$('#catalog-description'),
  status:$('#catalog-status-field'),
  rarity:$('#catalog-rarity'),
  wheelId:$('#wheel-editor-id'),
  wheelName:$('#wheel-editor-name'),
  wheelDescription:$('#wheel-editor-description'),
  wheelStatus:$('#wheel-editor-status'),
  wheelRarity:$('#wheel-editor-rarity'),
  engineSoundId:$('#engine-sound-editor-id'),
  engineSoundName:$('#engine-sound-editor-name'),
  engineSoundDescription:$('#engine-sound-editor-description'),
  engineSoundStatus:$('#engine-sound-editor-status'),
  engineSoundRarity:$('#engine-sound-editor-rarity'),
  cannonId:$('#cannon-editor-id'),
  cannonName:$('#cannon-editor-name'),
  cannonDescription:$('#cannon-editor-description'),
  cannonStatus:$('#cannon-editor-status'),
  cannonRarity:$('#cannon-editor-rarity'),
  projectileId:$('#projectile-editor-id'),
  projectileName:$('#projectile-editor-name'),
  projectileDescription:$('#projectile-editor-description'),
  projectileStatus:$('#projectile-editor-status'),
  projectileRarity:$('#projectile-editor-rarity'),
  nitroId:$('#nitro-editor-id'),
  nitroName:$('#nitro-editor-name'),
  nitroDescription:$('#nitro-editor-description'),
  nitroStatus:$('#nitro-editor-status'),
  nitroRarity:$('#nitro-editor-rarity'),
  nitroEditorMessage:$('#nitro-editor-message'),
  explosionId:$('#explosion-editor-id'),
  explosionName:$('#explosion-editor-name'),
  explosionDescription:$('#explosion-editor-description'),
  explosionStatus:$('#explosion-editor-status'),
  explosionRarity:$('#explosion-editor-rarity'),
  explosionEditorMessage:$('#explosion-editor-message'),
  tombstoneId:$('#tombstone-editor-id'),
  tombstoneName:$('#tombstone-editor-name'),
  tombstoneDescription:$('#tombstone-editor-description'),
  tombstoneStatus:$('#tombstone-editor-status'),
  tombstoneRarity:$('#tombstone-editor-rarity'),
  cloudPanel:$('#catalog-cloud-panel'),
  authModal:$('#catalog-auth-modal'),
  authSignedOut:$('#catalog-auth-signed-out'),
  authSignedIn:$('#catalog-auth-signed-in'),
  authNoAccess:$('#catalog-auth-no-access'),
  authNoAccessEmail:$('#catalog-auth-no-access-email'),
  authNoAccessId:$('#catalog-auth-no-access-id'),
  authNoAccessCopy:$('#catalog-auth-copy-id'),
  authNoAccessRetry:$('#catalog-auth-retry-access'),
  authNoAccessMessage:$('#catalog-auth-no-access-message'),
  authNoAccessLogout:$('#catalog-auth-no-access-logout'),
  authFirstSetup:$('#catalog-first-setup'),
  authEmail:$('#catalog-auth-email'),
  authPassword:$('#catalog-auth-password'),
  authLogin:$('#catalog-auth-login'),
  authBootstrap:$('#catalog-auth-bootstrap'),
  authLogout:$('#catalog-auth-logout'),
  authUser:$('#catalog-auth-user'),
  authRole:$('#catalog-auth-role'),
  authMessage:$('#catalog-auth-message'),
  cloudMessage:$('#catalog-cloud-message'),
  storageMode:$('#catalog-storage-mode'),
  storageTitle:$('#catalog-storage-title'),
  storageDetail:$('#catalog-storage-detail'),
  summaryCopy:$('#catalog-summary-copy'),
  dialog:$('#studio-dialog'),
  dialogIcon:$('#studio-dialog-icon'),
  dialogEyebrow:$('#studio-dialog-eyebrow'),
  dialogTitle:$('#studio-dialog-title'),
  dialogMessage:$('#studio-dialog-message'),
  dialogClose:$('#studio-dialog-close'),
  dialogCancel:$('#studio-dialog-cancel'),
  dialogConfirm:$('#studio-dialog-confirm'),
  economyModal:$('#catalog-economy-modal'),
  economySelection:$('#catalog-economy-selection'),
  economyMessage:$('#catalog-economy-message'),
  economyClose:$('#catalog-economy-close'),
  economyCancel:$('#catalog-economy-cancel'),
  economyApply:$('#catalog-economy-apply'),
};

const ECONOMY_SCREENS=Object.freeze({
  vehicle:elements.calibrator,
  wheel:elements.wheelEditor,
  engine_sound:elements.engineSoundEditor,
  cannon:elements.cannonEditor,
  projectile:elements.projectileEditor,
  nitro:elements.nitroEditor,
  explosion:elements.explosionEditor,
  tombstone:elements.tombstoneEditor,
});
const RARITY_PRICES=Object.freeze({common:300,rare:800,epic:1800,legendary:4500});

function installEconomyEditors(){
  Object.entries(ECONOMY_SCREENS).forEach(([type,screen])=>{
    const fields=screen?.querySelector('.catalog-metadata .metadata-fields');
    if(!fields||fields.querySelector('[data-economy-fields]'))return;
    const row=document.createElement('div');
    row.dataset.economyFields=type;
    row.className='catalog-economy-fields';
    row.innerHTML=`
      <label class="catalog-economy-toggle"><span>ADQUISICIÓN</span><select data-economy="mode"><option value="store">Canjeable por Coronas</option><option value="starter">Equipamiento starter</option><option value="unavailable">No disponible</option></select></label>
      <label><span>PRECIO EN CORONAS</span><input data-economy="price" type="number" min="1" max="999999" step="1" value="300" inputmode="numeric" /></label>
    `;
    fields.append(row);
    const mode=row.querySelector('[data-economy="mode"]');
    const price=row.querySelector('[data-economy="price"]');
    mode.addEventListener('change',()=>{
      price.disabled=mode.value!=='store';
      if(mode.value==='store'&&!(Number(price.value)>0))price.value='300';
    });
  });
}

function fillEconomyMetadata(item={},type=state.type){
  const screen=ECONOMY_SCREENS[type];
  const mode=screen?.querySelector('[data-economy="mode"]');
  const price=screen?.querySelector('[data-economy="price"]');
  if(!mode||!price)return;
  mode.value=item.starter===true?'starter':item.purchasable===false?'unavailable':'store';
  price.value=String(Math.max(1,Math.round(Number(item.priceCrowns)||RARITY_PRICES[item.rarity]||300)));
  price.disabled=mode.value!=='store';
}

function economyMetadata(type,rarity='common'){
  const screen=ECONOMY_SCREENS[type];
  const mode=screen?.querySelector('[data-economy="mode"]')?.value||'store';
  const price=screen?.querySelector('[data-economy="price"]')?.value;
  return {
    starter:mode==='starter',
    purchasable:mode==='store',
    priceCrowns:Math.max(1,Math.round(Number(price)||RARITY_PRICES[rarity]||300)),
  };
}

installEconomyEditors();

function safeText(value){
  return String(value??'').replace(/[&<>'"]/g,(character)=>({
    '&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#39;','"':'&quot;',
  })[character]);
}

function audioAssetProfile(mode,file,stats,kind){
  return {
    kind,
    mode:mode==='mp3'?'mp3':'procedural',
    fileName:mode==='mp3'?(file?.name||stats?.fileName||''):'',
    mediaType:mode==='mp3'?(file?.type||stats?.mediaType||'audio/mpeg'):'',
    byteSize:mode==='mp3'?(file?.size||stats?.byteSize||0):0,
    duration:mode==='mp3'?(stats?.duration||0):0,
    sampleRate:mode==='mp3'?(stats?.sampleRate||0):0,
    channels:mode==='mp3'?(stats?.channels||0):0,
  };
}

let dialogResolver=null;
let dialogPreviousFocus=null;

function closeStudioDialog(confirmed=false){
  if(elements.dialog.classList.contains('hidden'))return;
  elements.dialog.classList.add('hidden');
  const resolve=dialogResolver;
  dialogResolver=null;
  resolve?.(Boolean(confirmed));
  const previousFocus=dialogPreviousFocus;
  dialogPreviousFocus=null;
  if(previousFocus?.isConnected)requestAnimationFrame(()=>previousFocus.focus());
}

function showStudioDialog({
  eyebrow='CONFIRMACIÓN',
  title='Confirmar acción',
  message='Revisá los datos antes de continuar.',
  icon='✓',
  confirmLabel='CONFIRMAR',
  cancelLabel='CANCELAR',
  variant='default',
  focus='confirm',
}={}){
  if(dialogResolver)closeStudioDialog(false);
  dialogPreviousFocus=document.activeElement;
  elements.dialog.dataset.variant=variant;
  elements.dialogEyebrow.textContent=eyebrow;
  elements.dialogTitle.textContent=title;
  elements.dialogMessage.textContent=message;
  elements.dialogIcon.textContent=icon;
  elements.dialogConfirm.textContent=confirmLabel;
  elements.dialogCancel.textContent=cancelLabel;
  elements.dialog.classList.remove('hidden');
  requestAnimationFrame(()=>{
    (focus==='cancel'?elements.dialogCancel:elements.dialogConfirm).focus();
  });
  return new Promise((resolve)=>{
    dialogResolver=resolve;
  });
}

function typeLabel(type){
  return ({vehicle:'Autos',wheel:'Ruedas',engine_sound:'Sonidos de motor',cannon:'Cañones',projectile:'Proyectiles',nitro:'Nitro',explosion:'Explosiones',tombstone:'Lápidas',bundle:'Packs'})[type]||'Catálogo';
}

function localCatalogIdConflict(id,originalId=null){
  if(!id||id===originalId)return null;
  return state.items.find((item)=>item.id===id)||null;
}

function catalogIdConflictMessage(id,item=null){
  const itemKind=item?typeLabel(item.type).toLocaleLowerCase('es'):'catálogo';
  return `El ID técnico "${id}" ya existe en ${itemKind}. Elegí otro para no reemplazar el item anterior.`;
}

function cleanCatalogId(value,fallback='item-personalizado'){
  return String(value||fallback)
    .replace(/\.[^.]+$/,'')
    .normalize('NFD').replace(/[\u0300-\u036f]/g,'')
    .replace(/[^a-zA-Z0-9_-]+/g,'-')
    .replace(/^-+|-+$/g,'')
    .toLowerCase()||fallback;
}

function duplicateCatalogId(sourceId){
  const base=cleanCatalogId(`${sourceId}-copia`);
  const existingIds=new Set(state.items.map((item)=>item.id));
  if(!existingIds.has(base))return base;
  let suffix=2;
  while(existingIds.has(`${base}-${suffix}`))suffix+=1;
  return `${base}-${suffix}`;
}

let catalogIdGuards=[];

function setCatalogIdGuardState(guard,{valid,message}){
  guard.input.setAttribute('aria-invalid',String(!valid));
  guard.input.setCustomValidity(valid?'':message);
  guard.label.dataset.idState=valid?'success':'error';
  guard.message.textContent=message;
  if(valid){
    guard.saveButton.removeAttribute('data-catalog-id-conflict');
    guard.saveButton.removeAttribute('aria-disabled');
  }else{
    guard.saveButton.dataset.catalogIdConflict='true';
    guard.saveButton.setAttribute('aria-disabled','true');
  }
}

function updateCatalogIdGuard(guard,{announce=false,forcedMessage=''}={}){
  const id=cleanCatalogId(guard.input.value,guard.fallback);
  const conflict=forcedMessage?{}:localCatalogIdConflict(id,state.editingId);
  const valid=!forcedMessage&&!conflict;
  const message=valid
    ?'El ID está disponible y debe ser único dentro de todo el catálogo.'
    :forcedMessage||catalogIdConflictMessage(id,conflict);
  setCatalogIdGuardState(guard,{valid,message});
  if(announce&&!valid){
    guard.status.textContent=message;
    guard.status.dataset.state='error';
  }
  return {id,valid,message};
}

function refreshCatalogIdGuard(input){
  const guard=catalogIdGuards.find((entry)=>entry.input===input);
  if(guard)updateCatalogIdGuard(guard);
}

function refreshCatalogIdGuards(){
  catalogIdGuards.forEach((guard)=>updateCatalogIdGuard(guard));
}

function markActiveCatalogIdConflict(id,message){
  catalogIdGuards
    .filter((guard)=>!guard.screen.classList.contains('hidden'))
    .filter((guard)=>cleanCatalogId(guard.input.value,guard.fallback)===id)
    .forEach((guard)=>updateCatalogIdGuard(guard,{announce:true,forcedMessage:message}));
}

function installCatalogIdGuards(){
  const definitions=[
    ['wheel',elements.wheelId,'rueda-personalizada'],
    ['engine-sound',elements.engineSoundId,'sonido-motor-personalizado'],
    ['cannon',elements.cannonId,'canon-personalizado'],
    ['projectile',elements.projectileId,'proyectil-personalizado'],
    ['nitro',elements.nitroId,'nitro-personalizado'],
    ['explosion',elements.explosionId,'explosion-personalizada'],
    ['tombstone',elements.tombstoneId,'lapida-personalizada'],
  ];
  catalogIdGuards=definitions.map(([prefix,input,fallback])=>{
    const label=input.closest('label');
    const message=document.createElement('small');
    message.id=`${input.id}-availability`;
    message.className='catalog-id-message';
    message.setAttribute('aria-live','polite');
    input.insertAdjacentElement('afterend',message);
    input.setAttribute('aria-describedby',message.id);
    label.classList.add('catalog-id-field');
    const guard={
      prefix,input,fallback,label,message,
      saveButton:$(`#${prefix}-editor-save`),
      status:$(`#${prefix}-editor-message`),
      screen:$(`#${prefix}-editor-screen`),
    };
    input.addEventListener('input',()=>updateCatalogIdGuard(guard));
    input.addEventListener('blur',()=>updateCatalogIdGuard(guard,{announce:true}));
    guard.saveButton.addEventListener('click',(event)=>{
      const validation=updateCatalogIdGuard(guard,{announce:true});
      if(validation.valid)return;
      event.preventDefault();
      event.stopImmediatePropagation();
    },true);
    return guard;
  });
  refreshCatalogIdGuards();
}

async function assertCatalogIdAvailable(id,originalId=null){
  if(id===originalId)return;
  const localConflict=localCatalogIdConflict(id,originalId);
  const conflict=localConflict||await repository.get(id,{downloadAssets:false});
  if(!conflict)return;
  const message=catalogIdConflictMessage(id,conflict);
  markActiveCatalogIdConflict(id,message);
  const error=new Error(message);
  error.code='catalog-id-exists';
  throw error;
}

async function saveCatalogEditorItem(item,originalId=null){
  Object.assign(item,economyMetadata(item.type,item.rarity));
  const renamed=Boolean(originalId&&originalId!==item.id);
  try{
    await repository.save(item,{createOnly:!originalId||renamed});
  }catch(error){
    if(error.code==='catalog-id-exists'){
      markActiveCatalogIdConflict(item.id,error.message);
    }
    throw error;
  }
  if(renamed)await repository.remove(originalId);
}

function currentItems(){
  const query=elements.search.value.trim().toLocaleLowerCase('es');
  return state.items.filter((item)=>item.type===state.type&&(!query
    ||`${item.name} ${item.id} ${item.description||''}`.toLocaleLowerCase('es').includes(query)));
}

function catalogIcon(type){
  return ({vehicle:'▱',wheel:'◉',engine_sound:'♫',cannon:'➤',projectile:'◆',nitro:'✦',explosion:'✹',tombstone:'†'})[type]||'◆';
}

function catalogThumbnail(item){
  if(!item.thumbnailUrl)return catalogIcon(item.type);
  return `<img src="${safeText(item.thumbnailUrl)}" alt="" loading="lazy" decoding="async" />`;
}

function itemSummary(item){
  if(item.type==='wheel'){
    if(item.profile?.mode==='glb'){
      return `${item.assetFile?.name||item.profile?.asset?.fileName||'GLB importado'} · ${item.profile?.asset?.triangles||0} tris`;
    }
    return item.profile?.design?.rimStyle==='steel'
      ?`${item.profile?.design?.holeCount||0} huecos · chapa estampada · procedural`
      :`${item.profile?.design?.spokes||0} rayos · ${item.profile?.design?.tread||0} tacos · procedural`;
  }
  if(item.type==='cannon'){
    const soundLabel=item.profile?.sound?.mode==='mp3'
      ?` · disparo ${item.audioFile?.name||item.profile.sound.fileName||'MP3'}`
      :'';
    if(item.profile?.mode==='glb'){
      return `${item.assetFile?.name||item.profile?.asset?.fileName||'GLB importado'} · ${item.profile?.asset?.triangles||0} tris${soundLabel}`;
    }
    const styleLabel=({
      single:'tubo simple',
      twin:'tubo doble',
      bubble:'depósito redondo',
      rail:'rieles',
    })[item.profile?.design?.style]||'procedural';
    return `${styleLabel} · ${Number(item.profile?.design?.barrelLength||0).toFixed(2)} de largo · procedural${soundLabel}`;
  }
  if(item.type==='engine_sound'){
    const design=item.profile?.design||{};
    if(item.profile?.mode==='mp3'){
      return `${item.assetFile?.name||item.profile?.asset?.fileName||'Loop MP3'} · ${Number(item.profile?.asset?.duration||0).toFixed(2)} s · audio grabado`;
    }
    return `${design.wave||'sawtooth'} · ralentí ${Math.round(design.baseTone||0)} Hz · filtro ${Math.round(design.filter||0)} Hz`;
  }
  if(item.type==='projectile'){
    const importedSounds=['travel','impact'].filter(
      (slot)=>item.profile?.sounds?.[slot]?.mode==='mp3',
    ).length;
    const soundLabel=importedSounds
      ?` · ${importedSounds===2?'viaje e impacto MP3':'1 sonido MP3'}`
      :' · audio procedural';
    if(item.profile?.mode==='glb'){
      return `${item.assetFile?.name||item.profile?.asset?.fileName||'GLB importado'} · ${item.profile?.asset?.triangles||0} tris${soundLabel}`;
    }
    return `${item.profile?.design?.style||'dardo'} · escala ${Number(item.profile?.design?.scale||1).toFixed(2)}${soundLabel}`;
  }
  if(item.type==='nitro'){
    const design=item.profile?.design||{};
    const styleLabel=({
      flame:'llama',
      plasma:'plasma',
      confetti:'confeti',
    })[design.style]||'estela';
    const audioLabel=item.profile?.sound?.mode==='mp3'?' · loop MP3':'';
    return `${styleLabel} · ${design.nozzleCount||0} boquillas · ${design.particleCount||0} partículas${audioLabel}`;
  }
  if(item.type==='explosion'){
    const design=item.profile?.design||{};
    const styleLabel=({
      burst:'estallido',
      plasma:'plasma',
      firework:'fuegos artificiales',
      confetti:'confeti',
    })[design.style]||'explosión';
    const audioLabel=item.profile?.sound?.mode==='mp3'?' · MP3':'';
    return `${styleLabel} · ${design.particleCount||0} partículas · potencia ${Number(design.power||0).toFixed(1)}${audioLabel}`;
  }
  if(item.type==='tombstone'){
    const audioLabel=item.profile?.sound?.mode==='mp3'?' · aparición MP3':'';
    if(item.profile?.mode==='glb'){
      return `${item.assetFile?.name||item.profile?.asset?.fileName||'GLB importado'} · ${item.profile?.asset?.triangles||0} tris${audioLabel}`;
    }
    const styleLabel=({
      stone:'placa clásica',
      cone:'cono',
      holo:'holograma',
      blocks:'bloques',
    })[item.profile?.design?.style]||'procedural';
    return `${styleLabel} · escala ${Number(item.profile?.design?.scale||1).toFixed(2)} · procedural${audioLabel}`;
  }
  return `${item.bodyFile?.name||item.profile?.assets?.bodyFile||'Sin carrocería'} · rueda ${item.profile?.game?.defaultWheelId||'chrome_classic'} · motor ${item.profile?.game?.defaultEngineSoundId||'muscle_rumble'}`;
}

function setCloudMessage(message,status='ready'){
  elements.cloudMessage.textContent=message;
  elements.cloudMessage.dataset.state=status;
  elements.authMessage.textContent=message;
  elements.authMessage.dataset.state=status;
}

function setCloudBusy(busy){
  [
    elements.authLogin,
    elements.authBootstrap,
    elements.authLogout,
    elements.authNoAccessLogout,
    elements.authNoAccessCopy,
    elements.authNoAccessRetry,
  ].forEach((button)=>{button.disabled=busy;});
}

function renderCloudState(){
  document.querySelector('#studio-open-maps').disabled=!repository.canRead();
  mapStudio?.syncAuth();
  const configured=repository.configured;
  const signedIn=Boolean(repository.session);
  const hasAccess=repository.canRead();
  const showLogin=configured&&state.authReady&&!hasAccess;
  const showCredentials=showLogin&&!signedIn;
  const showNoAccess=showLogin&&signedIn;
  const modalWasHidden=elements.authModal.classList.contains('hidden');
  elements.authModal.classList.toggle('hidden',!showLogin);
  elements.authModal.setAttribute('aria-hidden',String(!showLogin));
  elements.cloudPanel.classList.toggle('hidden',!configured||!hasAccess);
  elements.authSignedOut.classList.toggle('hidden',!showCredentials);
  elements.authFirstSetup.classList.toggle('hidden',!showCredentials||!allowsFirstAdminSetup);
  elements.authNoAccess.classList.toggle('hidden',!showNoAccess);
  elements.authSignedIn.classList.toggle('hidden',!configured||!hasAccess);
  elements.storageMode.classList.toggle('cloud',repository.canRead());
  if(showCredentials&&modalWasHidden){
    queueMicrotask(()=>elements.authEmail.focus());
  }

  if(!configured){
    elements.storageTitle.textContent='CONFIGURACIÓN REQUERIDA';
    elements.storageDetail.textContent='Falta configurar Supabase';
    elements.summaryCopy.textContent='Catalog Studio necesita Supabase para leer y guardar el catálogo.';
    setCloudMessage('Faltan las variables públicas de Supabase.','warning');
    return;
  }
  if(!signedIn){
    elements.storageTitle.textContent='CATÁLOGO EN LA NUBE';
    elements.storageDetail.textContent='Iniciá sesión para continuar';
    elements.summaryCopy.textContent='Los items y borradores se guardan únicamente en Supabase.';
    setCloudMessage('Ingresá con una cuenta que ya pertenezca al equipo del Studio.');
    return;
  }

  elements.authNoAccessEmail.textContent=repository.session.user.email||'Cuenta sin email';
  elements.authNoAccessId.textContent=repository.session.user.id;
  elements.authUser.textContent=repository.session.user.email||'Usuario del equipo';
  elements.authRole.textContent=repository.membership
    ?`ROL ${repository.membership.role.toLocaleUpperCase('es')}`
    :'SIN ACCESO AL EQUIPO';
  if(repository.canRead()){
    elements.storageTitle.textContent='CATÁLOGO COMPARTIDO';
    elements.storageDetail.textContent=[
      'Supabase',
      publishesToR2?'R2':'Storage',
      repository.membership.role,
    ].join(' · ');
    elements.summaryCopy.textContent=publishesToR2
      ?'Supabase conserva datos y borradores; R2 recibe los assets publicados.'
      :'Supabase conserva datos, borradores y assets publicados.';
    setCloudMessage(repository.canPublish()
      ?`Conectado. Los próximos assets se publicarán en ${publishesToR2?'Cloudflare R2':'Supabase Storage'}.`
      :repository.canEdit()
        ?'Conectado. Podés crear y editar borradores.'
        :'Conectado en modo lectura.','success');
  }else{
    elements.storageTitle.textContent='ACCESO REQUERIDO';
    elements.storageDetail.textContent='Usuario sin rol';
    elements.summaryCopy.textContent='Este usuario todavía no pertenece al equipo del catálogo.';
    setCloudMessage('Un administrador debe agregar este usuario a team_members.','warning');
  }
}

function renderCatalog(){
  const visible=currentItems();
  const missingThumbnails=state.items.filter((item)=>
    THUMBNAIL_TYPES.has(item.type)&&!item.thumbnailUrl
  ).length;
  elements.title.textContent=typeLabel(state.type);
  elements.count.textContent=`${visible.length} ${visible.length===1?'ITEM':'ITEMS'}`;
  elements.newItem.disabled=!CREATE_LABELS[state.type]||!repository.canEdit();
  elements.newItem.textContent=CREATE_LABELS[state.type]||'EDITOR PRÓXIMAMENTE';
  elements.generateThumbnails.disabled=!repository.canEdit()||missingThumbnails===0;
  elements.configureEconomy.disabled=!repository.canPublish()||state.items.length===0;
  elements.generateThumbnails.textContent=missingThumbnails
    ?`CREAR MINIATURAS · ${missingThumbnails}`
    :'MINIATURAS LISTAS';
  elements.empty.classList.toggle('hidden',visible.length>0);
  const emptyCopy=EMPTY_COPY[state.type]||[
    `El editor de ${typeLabel(state.type).toLocaleLowerCase('es')} se conectará en la siguiente etapa.`,
    'Esta categoría ya está reservada y conservará el mismo flujo de borrador, revisión y publicación.',
  ];
  elements.empty.querySelector('strong').textContent=emptyCopy[0];
  elements.empty.querySelector('p').textContent=emptyCopy[1];
  elements.list.innerHTML=visible.map((item)=>`
    <article class="catalog-card" data-id="${safeText(item.id)}">
      <div class="catalog-card-icon${item.thumbnailUrl?' has-thumbnail':''}">${catalogThumbnail(item)}</div>
      <div class="catalog-card-copy">
        <span>${safeText(item.status||'draft')} · ${safeText(item.rarity||'common')} · ${item.starter?'STARTER':item.purchasable===false?'NO DISPONIBLE':`${Math.max(1,Number(item.priceCrowns)||250)} CORONAS`}</span>
        <h3>${safeText(item.name||item.id)}</h3>
        <p>${safeText(item.description||item.id)}</p>
        <small>${safeText(itemSummary(item))}</small>
      </div>
      <div class="catalog-card-actions">
        ${repository.canEdit()?'<button type="button" data-action="edit">EDITAR</button>':''}
        ${repository.canEdit()&&['nitro','explosion'].includes(item.type)
          ?'<button type="button" data-action="duplicate" class="duplicate">DUPLICAR</button>'
          :''}
        <button type="button" data-action="export">JSON</button>
        ${repository.canPublish()
          ?'<button type="button" data-action="publish" class="publish">PUBLICAR</button>'
          :''}
        ${repository.canEdit()?'<button type="button" data-action="delete" class="danger">ELIMINAR</button>':''}
      </div>
    </article>
  `).join('');
}

const REQUIRED_STARTER_TYPES=Object.freeze([
  'vehicle','wheel','engine_sound','cannon','projectile','nitro','explosion','tombstone',
]);

function closeEconomyModal(){
  elements.economyModal.classList.add('hidden');
  elements.economyMessage.textContent='';
  elements.economyMessage.removeAttribute('data-state');
}

function openEconomyModal(){
  const published=state.items.filter((item)=>item.status==='published');
  const legacyAllStarter=published.length>0&&published.every((item)=>item.starter===true);
  elements.economySelection.innerHTML=REQUIRED_STARTER_TYPES.map((type)=>{
    const items=published.filter((item)=>item.type===type);
    if(items.length===0)return '';
    return `<section class="catalog-economy-group" data-starter-type="${safeText(type)}">
      <strong>${safeText(typeLabel(type))}</strong>
      <div class="catalog-economy-options">${items.map((item)=>`
        <label class="catalog-economy-option">
          <input type="checkbox" value="${safeText(item.id)}" ${!legacyAllStarter&&item.starter?'checked':''} />
          ${item.thumbnailUrl?`<img src="${safeText(item.thumbnailUrl)}" alt="" />`:`<i>${catalogIcon(type)}</i>`}
          <span title="${safeText(item.id)}">${safeText(item.name||item.id)}</span>
        </label>`).join('')}</div>
    </section>`;
  }).join('');
  elements.economyMessage.textContent=legacyAllStarter
    ?'Detectamos el catálogo heredado: empezamos sin selecciones para que marques únicamente los starters reales.'
    :'Se conservaron las selecciones starter actuales. Podés modificarlas antes de aplicar.';
  elements.economyMessage.dataset.state='success';
  elements.economyModal.classList.remove('hidden');
  requestAnimationFrame(()=>elements.economySelection.querySelector('input')?.focus());
}

async function applyEconomyDefaults(){
  const starters=[...elements.economySelection.querySelectorAll('input:checked')]
    .map((input)=>input.value);
  const missing=REQUIRED_STARTER_TYPES.filter((type)=>{
    const group=elements.economySelection.querySelector(`[data-starter-type="${type}"]`);
    return group&&!group.querySelector('input:checked');
  });
  if(missing.length){
    elements.economyMessage.textContent=`Elegí al menos un starter en: ${missing.map(typeLabel).join(', ')}.`;
    elements.economyMessage.dataset.state='error';
    return;
  }
  elements.economyApply.disabled=true;
  elements.economyCancel.disabled=true;
  elements.economyMessage.textContent='Actualizando ofertas y creando un release nuevo…';
  elements.economyMessage.dataset.state='success';
  try{
    const result=await repository.normalizeEconomy(starters);
    await refresh();
    closeEconomyModal();
    setCloudMessage(
      `Economía configurada: ${starters.length} starters y release ${result?.release_number||'nuevo'} publicado.`,
      'success',
    );
  }catch(error){
    console.error(error);
    elements.economyMessage.textContent=error.message||'No se pudo configurar la economía.';
    elements.economyMessage.dataset.state='error';
  }finally{
    elements.economyApply.disabled=false;
    elements.economyCancel.disabled=false;
  }
}

async function refresh(){
  state.items=await repository.list();
  renderCatalog();
  refreshCatalogIdGuards();
}

function showCatalog(){
  elements.calibrator.classList.add('hidden');
  elements.wheelEditor.classList.add('hidden');
  elements.engineSoundEditor.classList.add('hidden');
  elements.cannonEditor.classList.add('hidden');
  elements.projectileEditor.classList.add('hidden');
  elements.nitroEditor.classList.add('hidden');
  elements.explosionEditor.classList.add('hidden');
  elements.tombstoneEditor.classList.add('hidden');
  elements.catalog.classList.remove('hidden');
}

function fillMetadata(item={}){
  elements.name.value=item.name||'';
  elements.description.value=item.description||'';
  elements.status.value=item.status||'draft';
  elements.rarity.value=item.rarity||'common';
  fillEconomyMetadata(item,'vehicle');
}

async function newVehicle(){
  state.editingId=null;
  fillMetadata();
  calibrator.clearWorkspace();
  calibrator.setCatalogEquipment(state.items);
  calibrator.open();
  await calibrator.initializeCatalogEquipment();
}

function fillWheelMetadata(item={}){
  elements.wheelId.value=item.id||'rueda-personalizada';
  elements.wheelName.value=item.name||'';
  elements.wheelDescription.value=item.description||'';
  elements.wheelStatus.value=item.status||'draft';
  elements.wheelRarity.value=item.rarity||'common';
  fillEconomyMetadata(item,'wheel');
  refreshCatalogIdGuard(elements.wheelId);
}

async function newWheel(){
  state.editingId=null;
  fillWheelMetadata();
  wheelEditor.clearWorkspace();
  wheelEditor.open();
}

async function editVehicle(id){
  const item=await repository.get(id);
  if(!item)return;
  state.editingId=id;
  fillMetadata(item);
  calibrator.setCatalogEquipment(state.items);
  calibrator.open();
  await calibrator.loadVehicleRecord(item);
}

async function editWheel(id){
  const item=await repository.get(id);
  if(!item)return;
  state.editingId=id;
  fillWheelMetadata(item);
  wheelEditor.open();
  await wheelEditor.loadRecord(item);
}

function fillEngineSoundMetadata(item={}){
  elements.engineSoundId.value=item.id||'sonido-motor-personalizado';
  elements.engineSoundName.value=item.name||'';
  elements.engineSoundDescription.value=item.description||'';
  elements.engineSoundStatus.value=item.status||'draft';
  elements.engineSoundRarity.value=item.rarity||'common';
  fillEconomyMetadata(item,'engine_sound');
  refreshCatalogIdGuard(elements.engineSoundId);
}

async function newEngineSound(){
  state.editingId=null;
  fillEngineSoundMetadata();
  engineSoundEditor.clearWorkspace();
  engineSoundEditor.open();
}

async function editEngineSound(id){
  const item=await repository.get(id);
  if(!item)return;
  state.editingId=id;
  fillEngineSoundMetadata(item);
  engineSoundEditor.open();
  await engineSoundEditor.loadRecord(item);
}

function fillCannonMetadata(item={}){
  elements.cannonId.value=item.id||'canon-personalizado';
  elements.cannonName.value=item.name||'';
  elements.cannonDescription.value=item.description||'';
  elements.cannonStatus.value=item.status||'draft';
  elements.cannonRarity.value=item.rarity||'common';
  fillEconomyMetadata(item,'cannon');
  refreshCatalogIdGuard(elements.cannonId);
}

async function newCannon(){
  state.editingId=null;
  fillCannonMetadata();
  cannonEditor.clearWorkspace();
  cannonEditor.setProjectileOptions(state.items.filter((item)=>item.type==='projectile'));
  cannonEditor.open();
}

async function editCannon(id){
  const item=await repository.get(id);
  if(!item)return;
  state.editingId=id;
  fillCannonMetadata(item);
  cannonEditor.setProjectileOptions(state.items.filter((entry)=>entry.type==='projectile'));
  cannonEditor.open();
  await cannonEditor.loadRecord(item);
}

function fillProjectileMetadata(item={}){
  elements.projectileId.value=item.id||'proyectil-personalizado';
  elements.projectileName.value=item.name||'';
  elements.projectileDescription.value=item.description||'';
  elements.projectileStatus.value=item.status||'draft';
  elements.projectileRarity.value=item.rarity||'common';
  fillEconomyMetadata(item,'projectile');
  refreshCatalogIdGuard(elements.projectileId);
}

async function newProjectile(){
  state.editingId=null;
  fillProjectileMetadata();
  projectileEditor.clearWorkspace();
  projectileEditor.open();
}

async function editProjectile(id){
  const item=await repository.get(id);
  if(!item)return;
  state.editingId=id;
  fillProjectileMetadata(item);
  projectileEditor.open();
  await projectileEditor.loadRecord(item);
}

function fillNitroMetadata(item={}){
  elements.nitroId.value=item.id||'nitro-personalizado';
  elements.nitroName.value=item.name||'';
  elements.nitroDescription.value=item.description||'';
  elements.nitroStatus.value=item.status||'draft';
  elements.nitroRarity.value=item.rarity||'common';
  fillEconomyMetadata(item,'nitro');
  refreshCatalogIdGuard(elements.nitroId);
}

async function newNitro(){
  state.editingId=null;
  fillNitroMetadata();
  nitroEditor.clearWorkspace();
  nitroEditor.open();
}

async function editNitro(id){
  const item=await repository.get(id);
  if(!item)return;
  state.editingId=id;
  fillNitroMetadata(item);
  nitroEditor.open();
  await nitroEditor.loadRecord(item);
}

function fillExplosionMetadata(item={}){
  elements.explosionId.value=item.id||'explosion-personalizada';
  elements.explosionName.value=item.name||'';
  elements.explosionDescription.value=item.description||'';
  elements.explosionStatus.value=item.status||'draft';
  elements.explosionRarity.value=item.rarity||'common';
  fillEconomyMetadata(item,'explosion');
  refreshCatalogIdGuard(elements.explosionId);
}

async function newExplosion(){
  state.editingId=null;
  fillExplosionMetadata();
  explosionEditor.clearWorkspace();
  explosionEditor.open();
}

async function editExplosion(id){
  const item=await repository.get(id);
  if(!item)return;
  state.editingId=id;
  fillExplosionMetadata(item);
  explosionEditor.open();
  await explosionEditor.loadRecord(item);
}

async function duplicateAnimatedEffect(id){
  const item=await repository.get(id);
  if(!item||!['nitro','explosion'].includes(item.type))return;
  const copyId=duplicateCatalogId(item.id);
  const copyMetadata={
    ...item,
    id:copyId,
    name:`${item.name||item.id} · copia`,
    status:'draft',
  };
  state.editingId=null;
  if(item.type==='nitro'){
    fillNitroMetadata(copyMetadata);
    nitroEditor.open();
    await nitroEditor.loadRecord(item);
    elements.nitroEditorMessage.textContent=`Copia de “${item.name||item.id}” lista. Se guardará como ${copyId}.`;
    elements.nitroEditorMessage.dataset.state='ready';
  }else{
    fillExplosionMetadata(copyMetadata);
    explosionEditor.open();
    await explosionEditor.loadRecord(item);
    elements.explosionEditorMessage.textContent=`Copia de “${item.name||item.id}” lista. Se guardará como ${copyId}.`;
    elements.explosionEditorMessage.dataset.state='ready';
  }
}

function fillTombstoneMetadata(item={}){
  elements.tombstoneId.value=item.id||'lapida-personalizada';
  elements.tombstoneName.value=item.name||'';
  elements.tombstoneDescription.value=item.description||'';
  elements.tombstoneStatus.value=item.status||'draft';
  elements.tombstoneRarity.value=item.rarity||'common';
  fillEconomyMetadata(item,'tombstone');
  refreshCatalogIdGuard(elements.tombstoneId);
}

async function newTombstone(){
  state.editingId=null;
  fillTombstoneMetadata();
  tombstoneEditor.clearWorkspace();
  tombstoneEditor.open();
}

async function editTombstone(id){
  const item=await repository.get(id);
  if(!item)return;
  state.editingId=id;
  fillTombstoneMetadata(item);
  tombstoneEditor.open();
  await tombstoneEditor.loadRecord(item);
}

function downloadProfile(item){
  const blob=new Blob([`${JSON.stringify(item.profile,null,2)}\n`],{type:'application/json'});
  const url=URL.createObjectURL(blob);
  const anchor=document.createElement('a');
  anchor.href=url;
  anchor.download=`${item.id}.${item.type}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}

async function captureCurrentThumbnail(type,id,design){
  if(type==='vehicle'){
    return createEditorThumbnailFile({
      editor:calibrator,
      id,
      type,
      rotationObject:calibrator.rig.root,
      rotationY:0,
      frame:()=>calibrator.frameVehicle(),
    });
  }
  if(type==='wheel'){
    const restorePreview=wheelEditor.prepareStandaloneThumbnail();
    try{
      return await createEditorThumbnailFile({
        editor:wheelEditor,
        id,
        type,
        cameraPosition:[4.1,2.45,4.3],
        target:[0,.12,0],
        rotationObject:wheelEditor.previewMount,
        rotationY:-.34,
      });
    }finally{
      restorePreview();
    }
  }
  if(type==='engine_sound')return createEngineSoundThumbnailFile(design,id);
  if(type==='cannon'){
    const restorePreview=cannonEditor.prepareStandaloneThumbnail();
    try{
      return await createEditorThumbnailFile({
        editor:cannonEditor,
        id,
        type,
        cameraPosition:[3.2,2.25,4.2],
        target:[0,.28,-.2],
        rotationObject:cannonEditor.previewMount,
        rotationY:-.32,
      });
    }finally{
      restorePreview();
    }
  }
  if(type==='projectile'){
    return createEditorThumbnailFile({
      editor:projectileEditor,
      id,
      type,
      cameraPosition:[2.8,1.8,3.8],
      target:[0,0,.1],
      rotationObject:projectileEditor.mount,
      rotationY:-.38,
    });
  }
  if(type==='nitro'){
    return createEditorThumbnailFile({
      editor:nitroEditor,
      id,
      type,
      cameraPosition:[3.9,2.5,4.7],
      target:[0,.65,.75],
      rotationObject:nitroEditor.previewMount,
      rotationY:-.42,
    });
  }
  if(type==='explosion'){
    return createEditorThumbnailFile({
      editor:explosionEditor,
      id,
      type,
      cameraPosition:[4,2.6,5],
      target:[0,.75,0],
      rotationObject:explosionEditor.previewMount,
      rotationY:-.28,
      prepare:()=>{
        explosionEditor.config.replay?.(explosionEditor.previewObject,design);
        const duration=Math.max(.2,Number(design?.duration)||1);
        const step=duration*.28/12;
        for(let index=0;index<12;index+=1){
          explosionEditor.config.updatePreview(
            explosionEditor.previewObject,
            design,
            step,
            false,
          );
        }
      },
    });
  }
  if(type==='tombstone'){
    return createEditorThumbnailFile({
      editor:tombstoneEditor,
      id,
      type,
      cameraPosition:[3,2.2,-4],
      target:[0,.38,0],
      rotationObject:tombstoneEditor.previewMount,
      rotationY:-.32,
    });
  }
  throw new Error(`El tipo ${type} todavía no tiene generador de miniaturas.`);
}

const calibrator=new VehicleCalibrator(elements.calibrator,{
  onOpen:()=>{
    elements.catalog.classList.add('hidden');
    elements.calibrator.classList.remove('hidden');
  },
  onClose:showCatalog,
  onSelectDefaultWheel:async(id)=>{
    const item=state.items.find((entry)=>entry.type==='wheel'&&entry.status==='published'&&entry.id===id);
    if(!item)throw new Error('Esta rueda no está publicada en el catálogo.');
    if(item?.profile?.mode==='glb'){
      const record=await repository.get(id);
      if(!record?.assetFile)throw new Error('La rueda GLB no tiene un archivo disponible.');
      return {
        assetFile:record.assetFile,
        inheritBodyFinish:true,
      };
    }
    const design=item?.profile?.design||WHEELS.find((entry)=>entry.id===id)||WHEELS[0];
    return {object:createCatalogProceduralWheel(design)};
  },
  onListComparisonVehicles:()=>state.items.filter(
    (item)=>item.type==='vehicle'&&item.id!==state.editingId
  ),
  onLoadComparisonVehicle:(id)=>repository.get(id),
  onValidateProfileName:(id)=>{
    const conflict=localCatalogIdConflict(id,state.editingId);
    return conflict
      ?{valid:false,message:catalogIdConflictMessage(id,conflict)}
      :{valid:true,message:'El ID está disponible y debe ser único dentro de todo el catálogo.'};
  },
  onSaveProfile:async({profile,bodyFile,wheelFile})=>{
    const originalId=state.editingId;
    await assertCatalogIdAvailable(profile.name,originalId);
    const thumbnailFile=await captureCurrentThumbnail('vehicle',profile.name);
    const item={
      id:profile.name,
      type:'vehicle',
      name:elements.name.value.trim()||profile.name,
      description:elements.description.value.trim(),
      status:elements.status.value,
      rarity:elements.rarity.value,
      profile,
      bodyFile,
      wheelFile,
      thumbnailFile,
      updatedAt:new Date().toISOString(),
    };
    await saveCatalogEditorItem(item,originalId);
    state.editingId=item.id;
    await refresh();
    return {
      handled:true,
      message:wheelFile
        ?`${item.name} quedó guardado con una rueda GLB de referencia.`
        :`${item.name} quedó guardado con rueda y sonido de motor predeterminados.`,
    };
  },
});

const wheelEditor=new WheelEditor(elements.wheelEditor,{
  onOpen:()=>{
    elements.catalog.classList.add('hidden');
    elements.wheelEditor.classList.remove('hidden');
  },
  onClose:showCatalog,
  onListVehicles:()=>state.items.filter((item)=>item.type==='vehicle'),
  onLoadVehicle:(id)=>repository.get(id),
  onSave:async({id,mode,design,assetFile,assetStats})=>{
    const originalId=state.editingId;
    await assertCatalogIdAvailable(id,originalId);
    const thumbnailFile=await captureCurrentThumbnail('wheel',id,design);
    const item={
      id,
      type:'wheel',
      name:elements.wheelName.value.trim()||id,
      description:elements.wheelDescription.value.trim(),
      status:elements.wheelStatus.value,
      rarity:elements.wheelRarity.value,
      profile:{
        schema:'toybox-wheel@1',
        id,
        name:elements.wheelName.value.trim()||id,
        description:elements.wheelDescription.value.trim(),
        rarity:elements.wheelRarity.value,
        mode,
        design,
        asset:mode==='glb'&&assetStats?{
          ...assetStats,
          fileName:assetFile?.name||assetStats.fileName||'',
        }:null,
      },
      assetFile:mode==='glb'?assetFile:null,
      thumbnailFile,
      updatedAt:new Date().toISOString(),
    };
    await saveCatalogEditorItem(item,originalId);
    state.editingId=id;
    await refresh();
    return {
      message:mode==='glb'
        ?`${item.name} quedó guardada con su rueda GLB.`
        :`${item.name} quedó guardada como diseño procedural editable.`,
    };
  },
});

const engineSoundEditor=new EngineSoundEditor(elements.engineSoundEditor,{
  onOpen:()=>{
    elements.catalog.classList.add('hidden');
    elements.engineSoundEditor.classList.remove('hidden');
  },
  onClose:showCatalog,
  onSave:async({id,mode,design,assetFile,assetStats})=>{
    const originalId=state.editingId;
    await assertCatalogIdAvailable(id,originalId);
    const name=elements.engineSoundName.value.trim()||id;
    const description=elements.engineSoundDescription.value.trim();
    const thumbnailFile=await captureCurrentThumbnail('engine_sound',id,design);
    const item={
      id,
      type:'engine_sound',
      name,
      description,
      status:elements.engineSoundStatus.value,
      rarity:elements.engineSoundRarity.value,
      profile:{
        schema:'toybox-engine-sound@2',
        id,name,description,
        rarity:elements.engineSoundRarity.value,
        mode,
        design,
        asset:mode==='mp3'?{
          kind:'engine-idle-loop',
          fileName:assetFile?.name||assetStats?.fileName||'',
          mediaType:assetFile?.type||assetStats?.mediaType||'audio/mpeg',
          byteSize:assetFile?.size||assetStats?.byteSize||0,
          duration:assetStats?.duration||0,
          sampleRate:assetStats?.sampleRate||0,
          channels:assetStats?.channels||0,
        }:null,
      },
      assetFile:mode==='mp3'?assetFile:null,
      thumbnailFile,
      updatedAt:new Date().toISOString(),
    };
    await saveCatalogEditorItem(item,originalId);
    state.editingId=id;
    await refresh();
    return {
      message:mode==='mp3'
        ?`${item.name} quedó guardado con su loop MP3 de motor.`
        :`${item.name} quedó guardado como sonido de motor procedural.`,
    };
  },
});

const cannonEditor=new CannonEditor(elements.cannonEditor,{
  onOpen:()=>{
    elements.catalog.classList.add('hidden');
    elements.cannonEditor.classList.remove('hidden');
  },
  onClose:showCatalog,
  onListVehicles:()=>state.items.filter((item)=>item.type==='vehicle'),
  onLoadVehicle:(id)=>repository.get(id),
  onSave:async({
    id,
    mode,
    design,
    assetFile,
    assetStats,
    soundMode,
    soundFile,
    soundStats,
  })=>{
    const originalId=state.editingId;
    await assertCatalogIdAvailable(id,originalId);
    const name=elements.cannonName.value.trim()||id;
    const description=elements.cannonDescription.value.trim();
    const thumbnailFile=await captureCurrentThumbnail('cannon',id,design);
    const item={
      id,
      type:'cannon',
      name,
      description,
      status:elements.cannonStatus.value,
      rarity:elements.cannonRarity.value,
      profile:{
        schema:'toybox-cannon@2',
        id,
        name,
        description,
        rarity:elements.cannonRarity.value,
        mode,
        design,
        asset:mode==='glb'&&assetStats?{
          ...assetStats,
          fileName:assetFile?.name||assetStats.fileName||'',
        }:null,
        sound:{
          kind:'cannon-shot',
          mode:soundMode,
          fileName:soundMode==='mp3'?(soundFile?.name||soundStats?.fileName||''):'',
          mediaType:soundMode==='mp3'?(soundFile?.type||soundStats?.mediaType||'audio/mpeg'):'',
          byteSize:soundMode==='mp3'?(soundFile?.size||soundStats?.byteSize||0):0,
          duration:soundMode==='mp3'?(soundStats?.duration||0):0,
          sampleRate:soundMode==='mp3'?(soundStats?.sampleRate||0):0,
          channels:soundMode==='mp3'?(soundStats?.channels||0):0,
        },
      },
      assetFile:mode==='glb'?assetFile:null,
      audioFile:soundMode==='mp3'?soundFile:null,
      thumbnailFile,
      updatedAt:new Date().toISOString(),
    };
    await saveCatalogEditorItem(item,originalId);
    state.editingId=id;
    await refresh();
    return {
      message:[
        mode==='glb'
          ?`${item.name} quedó guardado con su cañón GLB`
          :`${item.name} quedó guardado como cañón procedural editable`,
        soundMode==='mp3'?'y su disparo MP3.':'y su disparo procedural.',
      ].join(' '),
    };
  },
});

const projectileEditor=new ProjectileEditor(elements.projectileEditor,{
  onOpen:()=>{
    elements.catalog.classList.add('hidden');
    elements.projectileEditor.classList.remove('hidden');
  },
  onClose:showCatalog,
  onSave:async({
    id,
    mode,
    design,
    assetFile,
    assetStats,
    soundModes,
    soundFiles,
    soundStats,
  })=>{
    const originalId=state.editingId;
    await assertCatalogIdAvailable(id,originalId);
    const name=elements.projectileName.value.trim()||id;
    const description=elements.projectileDescription.value.trim();
    const thumbnailFile=await captureCurrentThumbnail('projectile',id,design);
    const item={
      id,
      type:'projectile',
      name,
      description,
      status:elements.projectileStatus.value,
      rarity:elements.projectileRarity.value,
      profile:{
        schema:'toybox-projectile@2',
        id,name,description,
        rarity:elements.projectileRarity.value,
        mode,
        design,
        asset:mode==='glb'&&assetStats?{
          ...assetStats,
          fileName:assetFile?.name||assetStats.fileName||'',
        }:null,
        sounds:{
          travel:audioAssetProfile(
            soundModes.travel,
            soundFiles.travel,
            soundStats.travel,
            'projectile-travel',
          ),
          impact:audioAssetProfile(
            soundModes.impact,
            soundFiles.impact,
            soundStats.impact,
            'projectile-impact',
          ),
        },
      },
      assetFile:mode==='glb'?assetFile:null,
      audioFiles:{
        travel:soundModes.travel==='mp3'?soundFiles.travel:null,
        impact:soundModes.impact==='mp3'?soundFiles.impact:null,
      },
      thumbnailFile,
      updatedAt:new Date().toISOString(),
    };
    await saveCatalogEditorItem(item,originalId);
    state.editingId=id;
    await refresh();
    return {
      message:`${item.name} quedó guardado con audio de viaje e impacto configurable.`,
    };
  },
});

const nitroEditor=new NitroEditor(elements.nitroEditor,{
  onOpen:()=>{
    elements.catalog.classList.add('hidden');
    elements.nitroEditor.classList.remove('hidden');
  },
  onClose:showCatalog,
  onSave:async({id,design,soundMode,soundFile,soundStats})=>{
    const originalId=state.editingId;
    await assertCatalogIdAvailable(id,originalId);
    const name=elements.nitroName.value.trim()||id;
    const description=elements.nitroDescription.value.trim();
    const thumbnailFile=await captureCurrentThumbnail('nitro',id,design);
    const item={
      id,
      type:'nitro',
      name,
      description,
      status:elements.nitroStatus.value,
      rarity:elements.nitroRarity.value,
      profile:{
        schema:'toybox-nitro@2',
        id,
        name,
        description,
        rarity:elements.nitroRarity.value,
        mode:'procedural',
        design,
        sound:audioAssetProfile(soundMode,soundFile,soundStats,'nitro-loop'),
      },
      audioFile:soundMode==='mp3'?soundFile:null,
      thumbnailFile,
      updatedAt:new Date().toISOString(),
    };
    await saveCatalogEditorItem(item,originalId);
    state.editingId=id;
    await refresh();
    return {
      message:soundMode==='mp3'
        ?`${item.name} quedó guardado con su loop MP3 de nitro.`
        :`${item.name} quedó guardado con sonido de nitro procedural.`,
    };
  },
});

const explosionEditor=new ExplosionEditor(elements.explosionEditor,{
  onOpen:()=>{
    elements.catalog.classList.add('hidden');
    elements.explosionEditor.classList.remove('hidden');
  },
  onClose:showCatalog,
  onSave:async({id,design,soundMode,soundFile,soundStats})=>{
    const originalId=state.editingId;
    await assertCatalogIdAvailable(id,originalId);
    const name=elements.explosionName.value.trim()||id;
    const description=elements.explosionDescription.value.trim();
    const thumbnailFile=await captureCurrentThumbnail('explosion',id,design);
    const item={
      id,
      type:'explosion',
      name,
      description,
      status:elements.explosionStatus.value,
      rarity:elements.explosionRarity.value,
      profile:{
        schema:'toybox-explosion@2',
        id,
        name,
        description,
        rarity:elements.explosionRarity.value,
        mode:'procedural',
        design,
        sound:audioAssetProfile(soundMode,soundFile,soundStats,'explosion'),
      },
      audioFile:soundMode==='mp3'?soundFile:null,
      thumbnailFile,
      updatedAt:new Date().toISOString(),
    };
    await saveCatalogEditorItem(item,originalId);
    state.editingId=id;
    await refresh();
    return {
      message:soundMode==='mp3'
        ?`${item.name} quedó guardada con su explosión MP3.`
        :`${item.name} quedó guardada con sonido de explosión procedural.`,
    };
  },
});

const tombstoneEditor=new TombstoneEditor(elements.tombstoneEditor,{
  onOpen:()=>{
    elements.catalog.classList.add('hidden');
    elements.tombstoneEditor.classList.remove('hidden');
  },
  onClose:showCatalog,
  onSave:async({
    id,
    mode,
    design,
    assetFile,
    assetStats,
    soundMode,
    soundFile,
    soundStats,
  })=>{
    const originalId=state.editingId;
    await assertCatalogIdAvailable(id,originalId);
    const name=elements.tombstoneName.value.trim()||id;
    const description=elements.tombstoneDescription.value.trim();
    const thumbnailFile=await captureCurrentThumbnail('tombstone',id,design);
    const item={
      id,
      type:'tombstone',
      name,
      description,
      status:elements.tombstoneStatus.value,
      rarity:elements.tombstoneRarity.value,
      profile:{
        schema:'toybox-tombstone@2',
        id,
        name,
        description,
        rarity:elements.tombstoneRarity.value,
        mode,
        design,
        asset:mode==='glb'&&assetStats?{
          ...assetStats,
          fileName:assetFile?.name||assetStats.fileName||'',
        }:null,
        sound:audioAssetProfile(soundMode,soundFile,soundStats,'tombstone-appearance'),
      },
      assetFile:mode==='glb'?assetFile:null,
      audioFile:soundMode==='mp3'?soundFile:null,
      thumbnailFile,
      updatedAt:new Date().toISOString(),
    };
    await saveCatalogEditorItem(item,originalId);
    state.editingId=id;
    await refresh();
    return {
      message:[
        mode==='glb'
          ?`${item.name} quedó guardada con su lápida GLB`
          :`${item.name} quedó guardada como lápida procedural editable`,
        soundMode==='mp3'?'y su aparición MP3.':'y su aparición procedural.',
      ].join(' '),
    };
  },
});

installCatalogIdGuards();

async function loadItemIntoThumbnailEditor(item){
  const design=item.profile?.design||{};
  if(item.type==='vehicle'){
    calibrator.setCatalogEquipment(state.items);
    await calibrator.loadVehicleRecord(item);
  }else if(item.type==='wheel'){
    await wheelEditor.loadRecord(item);
  }else if(item.type==='engine_sound'){
    await engineSoundEditor.loadRecord(item);
  }else if(item.type==='cannon'){
    cannonEditor.setProjectileOptions(state.items.filter((entry)=>entry.type==='projectile'));
    await cannonEditor.loadRecord(item);
  }else if(item.type==='projectile'){
    await projectileEditor.loadRecord(item);
  }else if(item.type==='nitro'){
    await nitroEditor.loadRecord(item);
  }else if(item.type==='explosion'){
    await explosionEditor.loadRecord(item);
  }else if(item.type==='tombstone'){
    await tombstoneEditor.loadRecord(item);
  }
  return captureCurrentThumbnail(item.type,item.id,design);
}

async function generateMissingThumbnails(){
  const candidates=state.items.filter((item)=>
    THUMBNAIL_TYPES.has(item.type)&&!item.thumbnailUrl
  );
  if(!candidates.length)return;
  const confirmed=await showStudioDialog({
    eyebrow:'CATÁLOGO VISUAL',
    title:'Crear miniaturas pendientes',
    message:`Se generarán ${candidates.length} imágenes desde las vistas reales de los editores. Cada artículo quedará como una nueva revisión; los que estaban publicados deberán publicarse otra vez para actualizarse dentro del juego.`,
    icon:'▣',
    confirmLabel:'CREAR MINIATURAS',
  });
  if(!confirmed)return;

  elements.generateThumbnails.disabled=true;
  elements.newItem.disabled=true;
  let completed=0;
  try{
    for(const summary of candidates){
      setCloudMessage(
        `Generando miniatura ${completed+1} de ${candidates.length}: ${summary.name||summary.id}…`,
        'ready',
      );
      const item=await repository.get(summary.id);
      if(!item)throw new Error(`${summary.name||summary.id} ya no existe.`);
      const thumbnailFile=await loadItemIntoThumbnailEditor(item);
      await repository.save({
        ...item,
        thumbnailFile,
        updatedAt:new Date().toISOString(),
      });
      completed+=1;
    }
    await refresh();
    setCloudMessage(
      `${completed} miniaturas creadas. Publicá las revisiones para verlas en el juego.`,
      'success',
    );
  }catch(error){
    console.error(error);
    await refresh().catch(()=>{});
    setCloudMessage(
      `${completed} de ${candidates.length} miniaturas creadas. ${error.message||'No se pudo completar el proceso.'}`,
      'error',
    );
  }finally{
    renderCatalog();
  }
}

elements.newItem.addEventListener('click',()=>{
  if(state.type==='vehicle')newVehicle();
  if(state.type==='wheel')newWheel();
  if(state.type==='engine_sound')newEngineSound();
  if(state.type==='cannon')newCannon();
  if(state.type==='projectile')newProjectile();
  if(state.type==='nitro')newNitro();
  if(state.type==='explosion')newExplosion();
  if(state.type==='tombstone')newTombstone();
});
elements.generateThumbnails.addEventListener('click',generateMissingThumbnails);
elements.configureEconomy.addEventListener('click',openEconomyModal);
elements.economyClose.addEventListener('click',closeEconomyModal);
elements.economyCancel.addEventListener('click',closeEconomyModal);
elements.economyApply.addEventListener('click',applyEconomyDefaults);
elements.economyModal.addEventListener('click',(event)=>{
  if(event.target===elements.economyModal)closeEconomyModal();
});
elements.search.addEventListener('input',renderCatalog);
document.querySelectorAll('[data-catalog-type]').forEach((button)=>{
  button.addEventListener('click',()=>{
    document.querySelectorAll('[data-catalog-type]').forEach((entry)=>entry.classList.toggle('selected',entry===button));
    state.type=button.dataset.catalogType;
    renderCatalog();
  });
});
elements.dialogClose.addEventListener('click',()=>closeStudioDialog(false));
elements.dialogCancel.addEventListener('click',()=>closeStudioDialog(false));
elements.dialogConfirm.addEventListener('click',()=>closeStudioDialog(true));
elements.dialog.addEventListener('click',(event)=>{
  if(event.target===elements.dialog)closeStudioDialog(false);
});
document.addEventListener('keydown',(event)=>{
  if(elements.dialog.classList.contains('hidden'))return;
  if(event.key==='Escape'){
    event.preventDefault();
    closeStudioDialog(false);
    return;
  }
  if(event.key==='Enter'){
    event.preventDefault();
    elements.dialogConfirm.click();
    return;
  }
  if(event.key!=='Tab')return;
  const focusable=[elements.dialogClose,elements.dialogCancel,elements.dialogConfirm];
  const current=focusable.indexOf(document.activeElement);
  const direction=event.shiftKey?-1:1;
  const next=focusable[(Math.max(0,current)+direction+focusable.length)%focusable.length];
  event.preventDefault();
  next.focus();
});
elements.list.addEventListener('click',async(event)=>{
  const action=event.target.closest('[data-action]')?.dataset.action;
  const id=event.target.closest('[data-id]')?.dataset.id;
  if(!action||!id)return;
  if(action==='edit'){
    const item=await repository.get(id);
    if(item?.type==='vehicle')await editVehicle(id);
    if(item?.type==='wheel')await editWheel(id);
    if(item?.type==='engine_sound')await editEngineSound(id);
    if(item?.type==='cannon')await editCannon(id);
    if(item?.type==='projectile')await editProjectile(id);
    if(item?.type==='nitro')await editNitro(id);
    if(item?.type==='explosion')await editExplosion(id);
    if(item?.type==='tombstone')await editTombstone(id);
  }
  if(action==='duplicate'){
    try{
      await duplicateAnimatedEffect(id);
    }catch(error){
      console.error(error);
      setCloudMessage(error.message||'No se pudo preparar la copia.','error');
    }
  }
  if(action==='export')downloadProfile(await repository.get(id));
  if(action==='publish'){
    const item=state.items.find((entry)=>entry.id===id);
    const confirmed=await showStudioDialog({
      eyebrow:'PUBLICACIÓN',
      title:'Publicar en el juego',
      message:`“${item?.name||id}” (${id}) se incluirá en una nueva versión del catálogo y quedará disponible para los jugadores.`,
      icon:'↑',
      confirmLabel:'PUBLICAR AHORA',
    });
    if(!confirmed)return;
    setCloudBusy(true);
    try{
      const release=await repository.publish(id);
      setCloudMessage(`Publicado en release #${release.release_number||'nuevo'}.`,'success');
      await refresh();
    }catch(error){
      console.error(error);
      setCloudMessage(error.message||'No se pudo publicar el item.','error');
    }finally{
      setCloudBusy(false);
    }
  }
  if(action==='delete'){
    const item=state.items.find((entry)=>entry.id===id);
    const published=item?.status==='published';
    const confirmed=await showStudioDialog({
      eyebrow:published?'RETIRAR DEL JUEGO':'ACCIÓN DESTRUCTIVA',
      title:published?'Archivar item publicado':'Eliminar item',
      message:published
        ?`“${item?.name||id}” se archivará y dejará de estar disponible en el juego a partir de una nueva versión del catálogo.`
        :`“${item?.name||id}” (${id}) se eliminará del catálogo. Esta acción no se puede deshacer.`,
      icon:'!',
      confirmLabel:published?'ARCHIVAR Y RETIRAR':'ELIMINAR DEFINITIVAMENTE',
      variant:'danger',
      focus:'cancel',
    });
    if(!confirmed)return;
    try{
      const result=await repository.remove(id);
      setCloudMessage(result.archived
        ?result.releaseNumber
          ?`${id} quedó archivado y retirado del juego en el release #${result.releaseNumber}.`
          :`${id} quedó archivado porque ya estaba publicado.`
        :`${id} fue eliminado.`,'success');
      await refresh();
    }catch(error){
      console.error(error);
      setCloudMessage(error.message||'No se pudo eliminar el item.','error');
    }
  }
});

async function authenticate(firstAdmin=false){
  const email=elements.authEmail.value.trim();
  const password=elements.authPassword.value;
  if(!email||password.length<6){
    setCloudMessage('Ingresá un email y una contraseña de al menos 6 caracteres.','warning');
    return;
  }
  setCloudBusy(true);
  try{
    const result=firstAdmin
      ?await repository.signUpFirstAdmin(email,password)
      :await repository.signIn(email,password);
    elements.authPassword.value='';
    renderCloudState();
    if(firstAdmin&&!result.session){
      setCloudMessage('Revisá tu email para confirmar la cuenta; después ingresá normalmente.','success');
    }
    await refresh();
  }catch(error){
    console.error(error);
    setCloudMessage(error.message||'No se pudo iniciar sesión.','error');
  }finally{
    setCloudBusy(false);
  }
}

elements.authLogin.addEventListener('click',()=>authenticate(false));
elements.authBootstrap.addEventListener('click',()=>authenticate(true));
elements.authPassword.addEventListener('keydown',(event)=>{
  if(event.key==='Enter')authenticate(false);
});
async function signOutCatalog(){
  setCloudBusy(true);
  try{
    await repository.signOut();
    renderCloudState();
    await refresh();
  }catch(error){
    console.error(error);
    setCloudMessage(error.message||'No se pudo cerrar la sesión.','error');
  }finally{
    setCloudBusy(false);
  }
}

elements.authLogout.addEventListener('click',signOutCatalog);
elements.authNoAccessLogout.addEventListener('click',signOutCatalog);
elements.authNoAccessRetry.addEventListener('click',async()=>{
  setCloudBusy(true);
  elements.authNoAccessMessage.textContent='Comprobando el acceso del equipo…';
  elements.authNoAccessMessage.dataset.state='ready';
  try{
    await repository.refreshMembership();
    renderCloudState();
    if(repository.canRead()){
      await refresh();
      setCloudMessage('Acceso confirmado. Bienvenido al Catalog Studio.','success');
    }else{
      elements.authNoAccessMessage.textContent='El acceso todavía está pendiente. Pedile al administrador que revise el UUID.';
      elements.authNoAccessMessage.dataset.state='warning';
    }
  }catch(error){
    console.error(error);
    elements.authNoAccessMessage.textContent=error.message||'No se pudo comprobar el acceso.';
    elements.authNoAccessMessage.dataset.state='error';
  }finally{
    setCloudBusy(false);
  }
});
elements.authNoAccessCopy.addEventListener('click',async()=>{
  const userId=repository.session?.user?.id;
  if(!userId)return;
  try{
    await navigator.clipboard.writeText(userId);
    elements.authNoAccessCopy.textContent='ID COPIADO';
    setTimeout(()=>{
      elements.authNoAccessCopy.textContent='COPIAR ID PARA EL ADMINISTRADOR';
    },1400);
  }catch(error){
    console.error(error);
    elements.authNoAccessCopy.textContent='NO SE PUDO COPIAR';
  }
});
repository.onAuthStateChange(async(_session,_membership,error)=>{
  if(error){
    console.error(error);
    setCloudMessage(error.message||'No se pudo actualizar la sesión.','error');
  }
  renderCloudState();
  try{await refresh();}catch(refreshError){console.error(refreshError);}
});

async function initializeStudio(){
  renderCloudState();
  try{
    await repository.initialize();
  }catch(error){
    console.error(error);
    setCloudMessage(
      error.message?.includes('claim_catalog_admin')
        ?'Falta ejecutar la migración SQL de Supabase.'
        :error.message||'No se pudo conectar con Supabase.',
      'error',
    );
  }finally{
    state.authReady=true;
    renderCloudState();
  }
  try{
    await refresh();
  }catch(error){
    console.error(error);
    elements.empty.classList.remove('hidden');
    elements.empty.querySelector('strong').textContent='No se pudo abrir el catálogo.';
  }
  document.body.classList.remove('studio-booting');
  const bootScreen=$('#studio-boot-screen');
  requestAnimationFrame(()=>requestAnimationFrame(()=>{
    bootScreen?.classList.add('done');
    setTimeout(()=>bootScreen?.remove(),380);
  }));
}

initializeStudio();
